/**
 * Password Strength Meter — KOPS-511
 *
 * Mirrors the backend policy in src/utils/password-policy.js so the
 * client-side validation is always in sync. If you change the rules
 * here, change them there too (or extract to a shared spec).
 *
 * Renders:
 *   - A four-segment strength bar (red → orange → yellow → green)
 *   - A live rules checklist so users see exactly what's missing
 *
 * Usage:
 *   <PasswordStrengthMeter password={password} email={email} />
 *
 * Designed for the navy+gold surfaces (ResetPasswordAIForward,
 * AgentSignup, ProfileSettings password change). All styles inline so
 * it works anywhere without importing the hub stylesheet.
 */

import React, { useMemo } from 'react';

const MIN_LENGTH = 12;
const MAX_LENGTH = 128;

const COMMON_PASSWORDS_SHORT = new Set([
  'password', 'password1', 'password123', 'passw0rd', 'p@ssword',
  'p@ssw0rd', '123456', '123456789', '12345678', '12345', 'qwerty',
  'qwerty123', 'admin', 'admin123', 'welcome', 'welcome1', 'welcome123',
  'letmein', 'iloveyou', 'princess', 'monkey', 'dragon', 'sunshine',
  'password2026', 'password2025', 'summer2026', 'winter2026',
  'kairos', 'kairosiq', 'aisearchportal', 'realtor', 'broker',
  'changeme', 'changeme123', 'default', 'default123', 'test123',
  'hello', 'hello123', 'trustno1', '1qaz2wsx', 'qwertyuiop',
]);

function emailLocalPart(email?: string): string | null {
  if (!email) return null;
  const at = email.indexOf('@');
  if (at < 1) return null;
  return email.slice(0, at).toLowerCase();
}

interface PolicyResult {
  rules: {
    length: boolean;
    classes: boolean;
    notCommon: boolean;
    notEmail: boolean;
    notTooLong: boolean;
  };
  score: number;
  valid: boolean;
}

export function evaluatePassword(password: string, email?: string): PolicyResult {
  const rules = {
    length: false,
    classes: false,
    notCommon: false,
    notEmail: false,
    notTooLong: false,
  };

  if (!password) {
    return { rules, score: 0, valid: false };
  }

  rules.length = password.length >= MIN_LENGTH;
  rules.notTooLong = password.length <= MAX_LENGTH;

  let classCount = 0;
  if (/[a-z]/.test(password)) classCount++;
  if (/[A-Z]/.test(password)) classCount++;
  if (/[0-9]/.test(password)) classCount++;
  if (/[^a-zA-Z0-9]/.test(password)) classCount++;
  rules.classes = classCount >= 3;

  const normalized = password.toLowerCase().trim();
  rules.notCommon = !COMMON_PASSWORDS_SHORT.has(normalized);

  const local = emailLocalPart(email);
  rules.notEmail = !local || local.length < 3 || !normalized.includes(local);

  const score = [rules.length, rules.classes, rules.notCommon, rules.notEmail].filter(Boolean).length;
  const valid = Object.values(rules).every(Boolean);

  return { rules, score, valid };
}

// Navy+gold palette (subset — just the tokens this component needs)
const colors = {
  navy: '#0F1E35',
  navy3: '#1E3557',
  gold2: '#E8B44A',
  muted: '#8FA3BF',
  red: '#e74c3c',
  orange: '#f39c12',
  yellow: '#f1c40f',
  green: '#27ae60',
  white: '#FFFFFF',
};

interface PasswordStrengthMeterProps {
  password: string;
  email?: string;
  onEvaluate?: (result: PolicyResult) => void;
}

const PasswordStrengthMeter: React.FC<PasswordStrengthMeterProps> = ({
  password,
  email,
  onEvaluate,
}) => {
  const result = useMemo(() => evaluatePassword(password, email), [password, email]);

  // Notify parent for enable/disable of the submit button.
  React.useEffect(() => {
    onEvaluate?.(result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.score, result.valid]);

  const strengthLabel =
    result.score === 0 ? 'Enter a password'
    : result.score === 1 ? 'Weak'
    : result.score === 2 ? 'Fair'
    : result.score === 3 ? 'Strong'
    : 'Excellent';

  const strengthColor =
    result.score <= 1 ? colors.red
    : result.score === 2 ? colors.orange
    : result.score === 3 ? colors.yellow
    : colors.green;

  return (
    <div style={{ marginBottom: '16px' }}>
      {/* Four-segment strength bar */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '6px' }}>
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            style={{
              flex: 1,
              height: '4px',
              borderRadius: '2px',
              background: result.score >= i ? strengthColor : colors.navy3,
              transition: 'background 0.2s',
            }}
          />
        ))}
      </div>

      {/* Strength label */}
      <div
        style={{
          fontSize: '10px',
          color: strengthColor,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '1px',
          marginBottom: '10px',
        }}
      >
        {strengthLabel}
      </div>

      {/* Rule checklist — only show when user has started typing */}
      {password.length > 0 && (
        <div
          style={{
            background: colors.navy,
            border: `1px solid ${colors.navy3}`,
            borderRadius: '6px',
            padding: '10px 12px',
            fontSize: '11px',
            lineHeight: 1.7,
          }}
        >
          <RuleRow
            ok={result.rules.length}
            label={`At least ${MIN_LENGTH} characters`}
          />
          <RuleRow
            ok={result.rules.classes}
            label="3 of: uppercase, lowercase, number, symbol"
          />
          <RuleRow
            ok={result.rules.notCommon}
            label="Not a commonly-used password"
          />
          <RuleRow
            ok={result.rules.notEmail}
            label="Doesn't contain your email address"
          />
          {!result.rules.notTooLong && (
            <RuleRow ok={false} label={`No longer than ${MAX_LENGTH} characters`} />
          )}
        </div>
      )}
    </div>
  );
};

const RuleRow: React.FC<{ ok: boolean; label: string }> = ({ ok, label }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: ok ? colors.green : colors.muted,
    }}
  >
    <span style={{ fontSize: '11px', fontWeight: 700, width: '12px' }}>
      {ok ? '✓' : '○'}
    </span>
    <span>{label}</span>
  </div>
);

export default PasswordStrengthMeter;
